import {
  Button,
  ModalFooterAction,
  useAlert,
  useToast,
} from "@apex/component-library";
import {
  Box,
  Flex,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Stack,
  Text,
} from "@chakra-ui/react";
import { useFormikContext } from "formik";
import { useRef } from "react";

import { useCreateCall } from "../../api/useCreateCall";
import { EventCodeLabels } from "../../types";
import { SUCCESS_TOAST } from "../../utils/constants";
import { getApiFormattedDate } from "../../utils/dateFormatter";
import { Currency, formatCurrency } from "../../utils/formatCurrency";
import { CreateCallFormValues } from "../CreateCallForm/types";
import ModalInfoItem from "../ModalInfoItem/";

export interface CreateCallPreviewModalProps {
  accountId: string;
  accountTitle: string;
  isOpen: boolean;
  onClose: () => void;
  setAccountId: (accountId: string) => void;
}

const CreateCallPreviewModal = ({
  accountId,
  accountTitle,
  isOpen,
  onClose,
  setAccountId,
}: CreateCallPreviewModalProps) => {
  const { resetForm, values } = useFormikContext<CreateCallFormValues>();
  const {
    callAmountDisplay,
    callAmountRaw,
    tradeDate,
    issueDate,
    dueDate,
    callType,
  } = values;

  const toast = useToast();
  const { removeAllAlerts } = useAlert();

  const { isLoading, mutate } = useCreateCall({
    onSuccess: data => {
      toast({
        ...SUCCESS_TOAST,
        description: "Margin call was created.",
      });
      removeAllAlerts();
      resetForm();
      setAccountId("");
    },
    onSettled: onClose,
  });

  const handleSubmit = () => {
    // Send callType directly to API - backend accepts IM (FEECM-17670 Req 6 Revised)
    mutate({
      account: accountId,
      eventCode: callType,
      amount: callAmountRaw!,
      tradeDate: getApiFormattedDate(tradeDate),
      issueDate: getApiFormattedDate(issueDate),
      dueDate: getApiFormattedDate(dueDate),
    });
  };

  const cancelRef = useRef<HTMLButtonElement>(null);

  return (
    <Modal
      motionPreset="slideInBottom"
      onClose={onClose}
      isOpen={isOpen}
      isCentered
      initialFocusRef={cancelRef}
    >
      <ModalOverlay />
      <ModalContent w="100%" maxW="600px">
        <ModalHeader textStyle="h1">Margin Call</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Stack pb={6} spacing={6}>
            <Text>
              Please review the details of your margin call before submitting.
            </Text>
            <Stack spacing={6}>
              <ModalInfoItem
                title="Account ID / Title"
                info={`${accountId.toUpperCase()} (${accountTitle})`}
                dataTestId="modal-account-id-title"
              />
              <ModalInfoItem
                title="Call Type"
                info={EventCodeLabels[callType]?.label}
                dataTestId="modal-call-type"
              />
              <ModalInfoItem
                title="Call Amount"
                info={formatCurrency(Currency.Usd, callAmountDisplay, 2)}
                dataTestId="modal-call-amount"
              />
              <ModalInfoItem
                title="Trade Date"
                info={tradeDate}
                dataTestId="modal-trade-date"
              />
              <ModalInfoItem
                title="Issue Date"
                info={issueDate}
                dataTestId="modal-issue-date"
              />
              <ModalInfoItem
                title="Due Date"
                info={dueDate}
                dataTestId="modal-due-date"
              />
            </Stack>
          </Stack>
        </ModalBody>
        <ModalFooterAction>
          <Flex>
            <Box ml="auto">
              <Button variant="white" ref={cancelRef} onClick={onClose}>
                Cancel
              </Button>
              <Button
                variant="primary"
                ml={3}
                onClick={handleSubmit}
                isLoading={isLoading}
                isDisabled={false}
                data-testid="confirm-create-call"
              >
                Confirm
              </Button>
            </Box>
          </Flex>
        </ModalFooterAction>
      </ModalContent>
    </Modal>
  );
};

export default CreateCallPreviewModal;
